import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

type Step = 1 | 2 | 3;

interface TestResult {
  valid: boolean;
  storeName?: string;
  storeUrl?: string;
  error?: string;
}

const DRAFT_KEY = "bc-create-draft";

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveDraft(data: { storeHash?: string; accessToken?: string; displayName?: string; testResult?: TestResult }) {
  try {
    const existing = loadDraft() ?? {};
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...existing, ...data }));
  } catch { /* ignore */ }
}

function clearDraft() {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

export default function BigCommerceCreate() {
  const navigate = useNavigate();
  const api = useApi();

  const draft = loadDraft();
  const [step, setStep] = useState<Step>(draft?.testResult?.valid ? 2 : 1);
  const [displayName, setDisplayName] = useState(draft?.displayName ?? "");
  const [storeHash, setStoreHash] = useState(draft?.storeHash ?? "");
  const [accessToken, setAccessToken] = useState(draft?.accessToken ?? "");
  const [testResult, setTestResult] = useState<TestResult | null>(draft?.testResult ?? null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionWarning, setConnectionWarning] = useState<string | null>(null);

  async function handleTestConnection() {
    if (!storeHash.trim()) { setError("Enter your BigCommerce store hash."); return; }
    if (!accessToken.trim()) { setError("Enter your BigCommerce access token."); return; }
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const resp = await api.admin.post("BigCommerce/Configs/Test", {
        storeHash: storeHash.trim(),
        accessToken: accessToken.trim()
      });
      const data = await resp.json() as TestResult;
      setTestResult(data);
      if (data.valid) {
        const name = displayName || data.storeName || "BigCommerce";
        if (!displayName) setDisplayName(name);
        saveDraft({ storeHash: storeHash.trim(), accessToken: accessToken.trim(), displayName: name, testResult: data });
        setStep(2);
      } else {
        setError(data.error || "Connection test failed. Check your credentials.");
      }
    } catch {
      setError("Failed to test connection. Please try again.");
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setConnectionWarning(null);
    try {
      const resp = await api.admin.post("BigCommerce/Configs", {
        displayName: displayName.trim() || testResult?.storeName || "BigCommerce",
        storeHash: storeHash.trim(),
        accessToken: accessToken.trim()
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body?.error ?? "Failed to save. Please try again.");
        return;
      }

      const body = await resp.json().catch(() => ({})) as { config?: unknown; connectionWarning?: string };
      clearDraft();
      if (body.connectionWarning) setConnectionWarning(body.connectionWarning);
      setStep(3);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  const steps = [
    { n: 1, label: "Connect" },
    { n: 2, label: "Settings" },
    { n: 3, label: "Done" }
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? "bg-cornflower-blue-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > s.n ? "✓" : s.n}
              </div>
              <span className={`text-sm ${step === s.n ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-3" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 1: Enter credentials ── */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Connect your BigCommerce store</h2>
          <p className="text-sm text-slate-500 mb-6">
            Create an API account in BigCommerce and enter your Store Hash and Access Token below.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800 space-y-2">
            <p className="font-medium">How to get your BigCommerce API credentials:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Go to <strong>BigCommerce Control Panel → Settings → API → Store-level API accounts</strong></li>
              <li>Click <strong>Create API account</strong> → <strong>Create V2/V3 API token</strong></li>
              <li>Give it a name (e.g. "Velaro Integration")</li>
              <li>Under <strong>OAuth Scopes</strong>, enable ALL of the following — missing any scope will cause specific AI skills to fail:
                <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5 text-xs">
                  <li><code className="bg-blue-100 px-1 rounded">Store Information: Read-only</code></li>
                  <li><code className="bg-blue-100 px-1 rounded">Products: Read-only</code> — catalog, categories, brands, inventory</li>
                  <li><code className="bg-blue-100 px-1 rounded">Customers: Modify</code> — read, create, and update customers</li>
                  <li><code className="bg-blue-100 px-1 rounded">Orders: Read-only</code> — order status, history, fulfillment</li>
                  <li><code className="bg-blue-100 px-1 rounded">Order Transactions: Read-only</code> — payment history</li>
                  <li><code className="bg-blue-100 px-1 rounded">Checkouts: Modify</code> — cart and checkout management</li>
                  <li><code className="bg-blue-100 px-1 rounded">Coupons: Read-only</code> — discount and coupon lookup</li>
                  <li><code className="bg-blue-100 px-1 rounded">Marketing: Read-only</code> — promotions</li>
                  <li><code className="bg-blue-100 px-1 rounded">Fulfillments: Read-only</code> — shipment tracking</li>
                  <li><code className="bg-blue-100 px-1 rounded">Content: Modify</code> — required for chat widget install</li>
                </ul>
              </li>
              <li>Click <strong>Save</strong> — copy the <strong>Store Hash</strong> and <strong>Access Token</strong></li>
            </ol>
            <a href="https://developer.bigcommerce.com/docs/rest-authentication/store-management-api" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">View BigCommerce API docs →</a>
          </div>

          <label className="flex flex-col gap-1 mb-4">
            <span className="text-sm font-medium">Store Hash</span>
            <input
              type="text"
              className="input font-mono text-sm"
              placeholder="abc123def or https://store-abc123def.mybigcommerce.com"
              value={storeHash}
              onChange={(e) => {
                let val = e.target.value.trim();
                // https://api.bigcommerce.com/stores/abc123def/v2/... → abc123def
                const apiMatch = val.match(/api\.bigcommerce\.com\/stores\/([a-z0-9]+)/i);
                // https://store-abc123def.mybigcommerce.com → abc123def
                const storeMatch = val.match(/store-([a-z0-9]+)\.mybigcommerce\.com/i);
                if (apiMatch) val = apiMatch[1];
                else if (storeMatch) val = storeMatch[1];
                setStoreHash(val);
                saveDraft({ storeHash: val });
              }}
              autoFocus
            />
            <span className="text-xs text-slate-400">Paste the hash or the full URL — we'll extract it automatically. Example: <code>store-<strong>abc123def</strong>.mybigcommerce.com</code></span>
          </label>

          <label className="flex flex-col gap-1 mb-4">
            <span className="text-sm font-medium">Access Token</span>
            <input
              type="password"
              className="input font-mono text-sm"
              placeholder="••••••••••••••••••••••••••••••••"
              value={accessToken}
              onChange={(e) => { setAccessToken(e.target.value); saveDraft({ accessToken: e.target.value }); }}
            />
            <span className="text-xs text-slate-400">The token from your BigCommerce API account. Never share this.</span>
          </label>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-end gap-2">
            <SecondaryBlueButton label="Cancel" onClick={() => { clearDraft(); navigate("/integrations"); }} />
            {testing ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Testing connection…</div>
            ) : (
              <PrimaryBlueButton label="Test & Continue" onClick={handleTestConnection} />
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: Settings ── */}
      {step === 2 && testResult?.valid && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-lg">✓</span>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Connected to BigCommerce</div>
              <div className="text-sm text-slate-500">
                Store: {testResult.storeName}
                {testResult.storeUrl && <span> — <a href={testResult.storeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{testResult.storeUrl}</a></span>}
              </div>
            </div>
          </div>

          <label className="flex flex-col gap-1 mb-6">
            <span className="text-sm font-medium">Display Name</span>
            <input
              className="input"
              placeholder="e.g. Production Store"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (testResult) saveDraft({ storeHash, accessToken, displayName: e.target.value, testResult });
              }}
            />
            <span className="text-xs text-slate-400">How this connection appears in Velaro.</span>
          </label>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <div className="flex justify-between">
            <SecondaryBlueButton label="Back" onClick={() => setStep(1)} />
            {saving ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Saving…</div>
            ) : (
              <PrimaryBlueButton label="Save & Finish" onClick={handleSave} />
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Done ── */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          {connectionWarning ? (
            <>
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-yellow-500 text-3xl">!</span>
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Credentials saved — connection issue</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-yellow-800 text-left">
                <p className="font-medium mb-1">Your credentials were saved, but the connection test failed:</p>
                <p>{connectionWarning}</p>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                Fix the issue in BigCommerce, then use "Test Connection" from the integration settings to verify.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-green-500 text-3xl">✓</span>
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">BigCommerce connected!</h2>
              <p className="text-sm text-slate-500 mb-6">
                Your BigCommerce store is now linked to Velaro. AI workflow nodes can query customers, orders, and products automatically.
              </p>
            </>
          )}
          <div className="flex justify-center gap-3">
            <SecondaryBlueButton label="Back to Integrations" onClick={() => navigate("/integrations")} />
            <PrimaryBlueButton label="Manage BigCommerce" onClick={() => navigate("/integrations/bigcommerce")} />
          </div>
        </div>
      )}
    </div>
  );
}
